import React from "react";
import {
  CreateEquipmentDto,
  EquipmentDto,
  MaintenanceAreaDto,
  equipmentApi,
} from "../../../services/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Props {
  equipment: EquipmentDto | null;
  areas: MaintenanceAreaDto[];
  onClose: () => void;
  onSaved: () => void;
  defaultAreaId?: string;
}

export const EquipmentFormDialog: React.FC<Props> = ({ equipment, areas, onClose, onSaved, defaultAreaId }) => {
  const isEdit = equipment != null;
  const [areaId, setAreaId] = React.useState(equipment?.areaId ?? defaultAreaId ?? areas[0]?.id ?? "");
  const [name, setName] = React.useState(equipment?.name ?? "");
  const [code, setCode] = React.useState(equipment?.code ?? "");
  const [type, setType] = React.useState(equipment?.type ?? "");
  const [location, setLocation] = React.useState(equipment?.location ?? "");
  const [brand, setBrand] = React.useState(equipment?.brand ?? "");
  const [model, setModel] = React.useState(equipment?.model ?? "");
  const [serialNumber, setSerialNumber] = React.useState(equipment?.serialNumber ?? "");
  const [purchaseDate, setPurchaseDate] = React.useState(
    equipment?.purchaseDate ? equipment.purchaseDate.slice(0, 10) : ""
  );
  const [notes, setNotes] = React.useState(equipment?.notes ?? "");
  const [isActive, setIsActive] = React.useState(equipment?.isActive ?? true);
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!name.trim()) { window.alert("El nombre es obligatorio"); return; }
    if (!areaId) { window.alert("Selecciona un área"); return; }

    setSaving(true);
    try {
      const payload: CreateEquipmentDto = {
        areaId,
        name: name.trim(),
        code: code.trim() || null,
        type: type.trim() || null,
        location: location.trim() || null,
        brand: brand.trim() || null,
        model: model.trim() || null,
        serialNumber: serialNumber.trim() || null,
        purchaseDate: purchaseDate || null,
        notes: notes.trim() || null,
        isActive,
      };
      if (isEdit && equipment) {
        await equipmentApi.update(equipment.id, payload);
      } else {
        await equipmentApi.create(payload);
      }
      onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar equipo" : "Nuevo equipo"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Área *</Label>
            <Select value={areaId} onValueChange={setAreaId}>
              <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label>Nombre *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Camión Iveco 1234, Envasadora #2" />
          </div>

          <div>
            <Label>Código interno</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
          <div>
            <Label>Tipo</Label>
            <Input value={type} onChange={(e) => setType(e.target.value)} placeholder="Camión, Envasadora, Cinta…" />
          </div>

          <div>
            <Label>Marca</Label>
            <Input value={brand} onChange={(e) => setBrand(e.target.value)} />
          </div>
          <div>
            <Label>Modelo</Label>
            <Input value={model} onChange={(e) => setModel(e.target.value)} />
          </div>

          <div>
            <Label>Nº de serie</Label>
            <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
          </div>
          <div>
            <Label>Fecha de compra</Label>
            <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <Label>Ubicación</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ej. Nave principal" />
          </div>

          <div className="md:col-span-2">
            <Label>Notas</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="flex items-center gap-2 md:col-span-2">
            <Checkbox id="eq-active" checked={isActive} onCheckedChange={(c) => setIsActive(c === true)} />
            <Label htmlFor="eq-active">Activo</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
